import classNames from 'classnames';
import UnconnectedMap from './UnconnectedMap';
import FeatureActions from '../actions/FeatureActions';
import {EditorEvent} from '../events';
import {EditorProps} from './props';
import {ClientMode} from "../enums";

/**
 * Názvy synchronizovaných properties specifických pro mapový editor.
 *
 * @ignore
 */
const syncedPropNames = [
    'readOnly',
    'layersMode',
    'styles',
    'color',
    'lineWidth',
    'tools',
    'contextTools',
    'keyboardShortcuts',
    'featureDeleteHandler',
    'featurePrintEnabled',
    'printSelectedLayers', // Deprecated.
    'printInitialFeatures', // Deprecated.
];

/**
 * Mapový editor nenapojený na store.
 *
 * @ignore
 */
export default class UnconnectedMapEditor extends UnconnectedMap<EditorProps, EditorEvent> {
    /**
     * Vrátí jména properties, které synchronizujeme s mapovým serverem při inicializaci.
     *
     * @returns Jména properties.
     */
    protected getInitialSyncedPropNames(): string[] {
        return [...super.getInitialSyncedPropNames(), ...syncedPropNames];
    }

    /**
     * Vrátí jména properties, které synchronizujeme s mapovým serverem při změně.
     *
     * @returns Jména properties.
     */
    protected getSyncedPropNames(): string[] {
        return [...super.getSyncedPropNames(), ...syncedPropNames];
    }

    /**
     * Namapuje hodnotu synchronizované property.
     *
     * @param props - Properties ze kterých chceme vzít hodnotu.
     * @param name - Název property.
     * @returns Mapovaná hodnota property.
     */
    protected getSyncedPropValue(props, name): any {
        switch (name) {
            // Namísto callbacku, chceme předat pouze boolean informaci, že je callback zaregistrován.
            case 'featureDeleteHandler':
                return props[name] != null;

            default:
                return super.getSyncedPropValue(props, name);
        }
    }

    /**
     * Vrátí cestu mapové stránky v URL.
     *
     * @returns Cesta.
     */
    protected getPath(): string {
        return this.props.clientMode ?? ClientMode.EMBED_EDITOR;
    }

    /**
     * Provede výchozí synchronizaci properties.
     */
    protected postInitialSyncedProps(): void {
        super.postInitialSyncedProps();

        const {dispatch, area, initialFeatures, initialFeaturesOptions} = this.props;
        if (initialFeatures && initialFeatures.length) {
            dispatch(FeatureActions.loadFeatures(area, initialFeatures, initialFeaturesOptions));
        }
    }

    /**
     * Zpracuje nerozpoznanou zprávu od frame.
     *
     * @param message - Zpráva.
     */
    protected handleUnrecognizedMessage(message): void {
        switch (message.type) {
            case 'DELETE_FEATURES_REQUEST':
                this.props.featureDeleteHandler &&
                    this.props.featureDeleteHandler({
                        featurePointers: message.featurePointers,
                    });
                break;

            default:
                super.handleUnrecognizedMessage(message);
                break;
        }
    }

    /**
     * Vykreslí komponentu.
     *
     * @returns Element.
     */
    render(): JSX.Element | null {
        const {
            className: customClassName,
            readOnly,
            layersMode,
            styles,
            color,
            lineWidth,
            tools,
            contextTools,
            keyboardShortcuts,
            featureDeleteHandler,
            featurePrintEnabled,
            initialFeatures,
            initialFeaturesOptions,
            printSelectedLayers, // Deprecated.
            printInitialFeatures, // Deprecated.
            clientMode,
            topicChangeEnabled,
            ...otherProps
        } = this.props;

        const className = classNames(
            'map-editor',
            'mapio-feature-editor', // Pro zpětnou kompatibilitu.
            {'read-only': readOnly},
            customClassName,
        );

        return this.renderFrame({className, ...otherProps});
    }
}
